import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../api/adminApi';
import { Playground } from '../playground/Playground';
import { AnalysisList } from './AnalysisList';
import { HintList } from './HintList';
import { SolutionSplitterList } from './SolutionSplitterList';
import { ConceptLabList } from './ConceptLabList';
import { JudgeRunList } from './JudgeRunList';
import { GenerationList } from './GenerationList';
import { ExtractionList } from './ExtractionList';
import { LearningMomentsList } from './LearningMomentsList';
import { QuestionSessionReportList } from './QuestionSessionReportList';
import { TestReportList } from './TestReportList';
import { MarkAttemptList } from './MarkAttemptList';
import { CohortClusterList } from './CohortClusterList';
import { PipelineMetricsTab } from './PipelineMetricsTab';
import { HintQualityTab } from './HintQualityTab';
import './Operations.css';
import { TabStrip } from '@shared-ui/components';
import { OPERATION_TYPES, OPERATION_TYPE_KEYS, type OperationType } from '@padcanvas/shared';

/** Top-level tabs. 'operations' hosts the operation views above. */
type PageTabKey = 'operations' | 'performance' | 'pipeline' | 'playground' | 'hintquality';
type MetaTabKey = Exclude<PageTabKey, 'operations'>;

/**
 * THE list. Every LLM operation, and what renders its runs.
 *
 * An exhaustive `Record` over the canonical registry, so adding an operation
 * type stops compiling here until somebody decides where its runs are
 * observable. `null` means "deliberately has no run list", not "not done yet".
 *
 * The dropdown, its labels and its ordering are all derived from this and from
 * `OPERATION_TYPES` — there is no second array of keys, no parallel union, and
 * no view-name vocabulary. That duplication is how `testReport` shipped with a
 * pipeline, a prompt and an async job and no way to see any of it: the registry
 * knew about it and the page's own list did not.
 */
const RUN_VIEW: Record<OperationType, (() => React.ReactNode) | null> = {
  analysis: () => <AnalysisList />,
  hint: () => <HintList />,
  conceptLab: () => <ConceptLabList />,
  learningMoments: () => <LearningMomentsList />,
  // Sitting-wide runs sit in the same list as per-question ones — one row
  // per call either way, and the row says which kind it is.
  learningMomentsSitting: () => <LearningMomentsList />,
  splitSolutions: () => <SolutionSplitterList />,
  // No run list of its own: each reading is shown on the analysis it read,
  // beside the served answer, which is the only place it means anything.
  readAnswer: null,
  readAnswerList: null,
  // Read inside the solution-splitter job; its readings and counters sit on
  // that run's record, so the splitter's list is where they are seen.
  readSolutionPages: null,
  markAttempt: () => <MarkAttemptList />,
  testReport: () => <TestReportList />,
  questionSessionReport: () => <QuestionSessionReportList />,
  clusterMisconceptions: () => <CohortClusterList />,
  // Two judges, one list — the same surface filtered by judge kind.
  judgeAnswerEquiv: () => <JudgeRunList />,
  judgeQuality: () => <JudgeRunList />,
  // Both variation paths land in the generation list.
  generateVariations: () => <GenerationList />,
  generateVariationsFromQuestions: () => <GenerationList />,
  // The extraction list covers the PDF and Word paths and the enrichment pass
  // that runs over whatever either produced.
  extractVerbatim: () => <ExtractionList />,
  extractVerbatimText: () => <ExtractionList />,
  enrichQuestions: () => <ExtractionList />,
  // No run list of its own: a mark-scheme parse is visible on the paper it was
  // uploaded to, and nobody has needed to browse them across papers.
  extractMarkScheme: null,
  // No run list of its own: an answer-key read runs inside the paper's import
  // job and its calls are recorded on that import in Model-Runs.
  extractAnswerKey: null,
  // Conversational operations produce turns rather than runs; a per-run list
  // would be a chat log, which Session Traces already is.
  textchat: null,
  realtimeVoice: null,
  // No run list of its own yet — nobody has needed to browse chemistry-lab
  // mix outcomes across students. Same "not done, not exempt" state as
  // extractMarkScheme above.
  chemistryLabMix: null,
  // Nor the overlay, for the same reason and with the same caveat. Both are
  // fully visible in Model-Runs — cost, tokens, latency, failures — and both
  // have a curation queue of their own under Chemistry Lab, which is where a
  // person actually reads outcomes. What is missing is browsing them as RUNS.
  chemistryLabExcess: null,
  // Compiled by the batch tooling and read on the Scenes page, which is where
  // coverage and calibration are judged — there is no run list to browse yet.
  // Matches its classification in `pipelineRunnerCoverage.test.ts`:
  // template-less until it has a runtime path.
  compileScene: null,
};

/** URLs in the wild carry the hand-made keys this page used before the list was
 *  derived. Kept so a bookmarked `?tab=hints` still opens hints. */
const LEGACY_TAB_KEY: Record<string, OperationType> = {
  submissions: 'analysis',
  hints: 'hint',
  solutionsplit: 'splitSolutions',
  conceptlab: 'conceptLab',
  learningmoments: 'learningMoments',
  evaljudge: 'judgeAnswerEquiv',
  generation: 'generateVariations',
  extraction: 'extractVerbatim',
  questionsessionreport: 'questionSessionReport',
  testreport: 'testReport',
  markattempt: 'markAttempt',
  cohortcluster: 'clusterMisconceptions',
};

/** The operations that have a view, in registry order, labelled from the
 *  registry. Derived — never hand-listed. */
const OPERATION_VIEWS = OPERATION_TYPE_KEYS
  .filter(op => RUN_VIEW[op])
  .map(op => ({ key: op, label: OPERATION_TYPES[op].label, render: RUN_VIEW[op]! }));

const DEFAULT_OPERATION: OperationType = 'analysis';

const resolveOperation = (k: string): OperationType | null =>
  (OPERATION_VIEWS.some(v => v.key === k) ? (k as OperationType) : (LEGACY_TAB_KEY[k] ?? null));

interface PromptPerformance {
  promptId: string;
  promptName: string;
  totalEvaluations: number;
  avgAccuracy: number;
  avgFeedbackQuality: number;
  avgCompleteness: number;
  avgOverall: number;
}

interface ProviderPerformance {
  provider: string;
  totalEvaluations: number;
  avgAccuracy: number;
  avgFeedbackQuality: number;
  avgCompleteness: number;
  avgOverall: number;
}

interface PerformanceData {
  byPrompt: PromptPerformance[];
  byProvider: ProviderPerformance[];
  totalEvaluations: number;
}

const getScoreColor = (score: number): string => {
  if (score >= 4) return 'score-good';
  if (score >= 3) return 'score-medium';
  return 'score-low';
};




export function Operations() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [playgroundQuestionId, setPlaygroundQuestionId] = useState<string | null>(null);
  const [playgroundSubmissionId, setPlaygroundSubmissionId] = useState<string | null>(null);

  const urlTab = searchParams.get('tab') ?? '';
  const [activeTab, setActiveTab] = useState<PageTabKey>(
    resolveOperation(urlTab) || urlTab === '' ? 'operations' : (urlTab as PageTabKey));
  const [activeOperation, setActiveOperation] = useState<OperationType>(
    resolveOperation(urlTab) ?? DEFAULT_OPERATION);

  // Handle navigation from submission detail → playground
  useEffect(() => {
    const state = location.state as { openPlayground?: boolean; questionId?: string; submissionId?: string } | null;
    if (state?.openPlayground) {
      setActiveTab('playground');
      if (state.questionId) setPlaygroundQuestionId(state.questionId);
      if (state.submissionId) setPlaygroundSubmissionId(state.submissionId);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  /** The URL keeps carrying the operation key while the Operations tab is
   *  selected, so links like ?tab=hints still resolve. */
  const syncUrl = (tab: PageTabKey, operation: OperationType) => {
    const key = tab === 'operations' ? operation : tab;
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (key === DEFAULT_OPERATION) params.delete('tab');
      else params.set('tab', key);
      return params;
    }, { replace: true });
  };

  const handleTabChange = (tab: PageTabKey) => {
    setActiveTab(tab);
    syncUrl(tab, activeOperation);
  };

  const handleOperationChange = (operation: OperationType) => {
    setActiveOperation(operation);
    syncUrl('operations', operation);
  };

  const { data: performanceData, isLoading: performanceLoading } = useQuery({
    queryKey: ['performance'],
    queryFn: () => adminApi.getPerformanceStats(),
    enabled: activeTab === 'performance',
  });

  const renderPerformanceTab = () => {
    if (performanceLoading) return <div className="loading">Loading performance data...</div>;
    if (!performanceData) return <div className="empty-state">No performance data available yet.</div>;

    const perfData = performanceData as PerformanceData;

    return (
      <div className="performance-content">
        <div className="performance-summary">
          <span className="summary-stat">
            Total Manual Evaluations: <strong>{perfData.totalEvaluations}</strong>
          </span>
        </div>

        {perfData.totalEvaluations === 0 ? (
          <div className="empty-state">
            <p>No manual evaluations have been recorded yet.</p>
            <p>Evaluate experiments in the submission detail view to see performance metrics here.</p>
          </div>
        ) : (
          <>
            <div className="performance-section">
              <h3>Performance by Provider</h3>
              <table className="dt dt--compact">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Evaluations</th>
                    <th>Accuracy</th>
                    <th>Feedback Quality</th>
                    <th>Completeness</th>
                    <th>Overall</th>
                  </tr>
                </thead>
                <tbody>
                  {perfData.byProvider.map((p) => (
                    <tr key={p.provider}>
                      <td><span className="provider-badge">{p.provider}</span></td>
                      <td>{p.totalEvaluations}</td>
                      <td><span className={`score-cell ${getScoreColor(p.avgAccuracy)}`}>{p.avgAccuracy.toFixed(1)}</span></td>
                      <td><span className={`score-cell ${getScoreColor(p.avgFeedbackQuality)}`}>{p.avgFeedbackQuality.toFixed(1)}</span></td>
                      <td><span className={`score-cell ${getScoreColor(p.avgCompleteness)}`}>{p.avgCompleteness.toFixed(1)}</span></td>
                      <td><span className={`score-cell ${getScoreColor(p.avgOverall)}`}><strong>{p.avgOverall.toFixed(1)}</strong></span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="performance-section">
              <h3>Performance by Prompt</h3>
              <table className="dt dt--compact">
                <thead>
                  <tr>
                    <th>Prompt</th>
                    <th>Evaluations</th>
                    <th>Accuracy</th>
                    <th>Feedback Quality</th>
                    <th>Completeness</th>
                    <th>Overall</th>
                  </tr>
                </thead>
                <tbody>
                  {perfData.byPrompt.map((p) => (
                    <tr key={p.promptId}>
                      <td><span className="prompt-name">{p.promptName}</span></td>
                      <td>{p.totalEvaluations}</td>
                      <td><span className={`score-cell ${getScoreColor(p.avgAccuracy)}`}>{p.avgAccuracy.toFixed(1)}</span></td>
                      <td><span className={`score-cell ${getScoreColor(p.avgFeedbackQuality)}`}>{p.avgFeedbackQuality.toFixed(1)}</span></td>
                      <td><span className={`score-cell ${getScoreColor(p.avgCompleteness)}`}>{p.avgCompleteness.toFixed(1)}</span></td>
                      <td><span className={`score-cell ${getScoreColor(p.avgOverall)}`}><strong>{p.avgOverall.toFixed(1)}</strong></span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="performance-legend">
          <span className="legend-item"><span className="score-cell score-good">4.0+</span> Good</span>
          <span className="legend-item"><span className="score-cell score-medium">3.0-3.9</span> Moderate</span>
          <span className="legend-item"><span className="score-cell score-low">&lt;3.0</span> Needs Improvement</span>
        </div>
      </div>
    );
  };

  // Single source of truth for the Operations page. 'operation' views live
  // under the Operations tab (picked via a dropdown, so they scale); 'tab'
  // views are their own top-level tabs. Add a new operation once here and it
  // shows up in the dropdown automatically.
  // Meta tabs only. The operation views are derived from the registry
  // (`OPERATION_VIEWS`) and are not listed here — that separation is the point:
  // an operation cannot be forgotten, and a tab that is not an operation cannot
  // pretend to be one.
  const META_TABS: { key: MetaTabKey; label: string; render: () => React.ReactNode }[] = [
    { key: 'performance', label: 'Model & Prompt Performance', render: () => renderPerformanceTab() },
    { key: 'pipeline', label: 'Pipeline Metrics', render: () => <PipelineMetricsTab /> },
    // A report over hint data — no runs, no prompts — so it belongs with the
    // other analysis views rather than in the operation dropdown.
    { key: 'hintquality', label: 'Hint Quality', render: () => <HintQualityTab /> },
    { key: 'playground', label: 'Playground', render: () => <Playground questionId={playgroundQuestionId} submissionId={playgroundSubmissionId} /> },
  ];

  const operationViews = OPERATION_VIEWS;
  const metaTabs = META_TABS;
  const isOperationTab = activeTab === 'operations';
  // The view to render: the picked operation, or the meta tab itself.
  const activeView = (isOperationTab
    ? operationViews.find(v => v.key === activeOperation)
    : metaTabs.find(t => t.key === activeTab))
    ?? operationViews[0];

  return (
    <div className="submissions-page">
      <div className="page-header">
        <h1>Operations</h1>
      </div>

      <TabStrip
        ariaLabel="Operations views"
        active={activeTab}
        onChange={handleTabChange}
        tabs={[
          { key: 'operations', label: 'Operations' },
          ...metaTabs.map(t => ({ key: t.key as PageTabKey, label: t.label })),
        ]}
      />


      {isOperationTab && (
        <div className="operations-type-bar">
          <label className="operations-type-label" htmlFor="operation-select">Operation</label>
          <select
            id="operation-select"
            className="operations-type-select"
            value={activeOperation}
            onChange={(e) => handleOperationChange(e.target.value as OperationType)}
          >
            {operationViews.map(op => (
              <option key={op.key} value={op.key}>{op.label}</option>
            ))}
          </select>
        </div>
      )}

      <div className="tab-content">
        {activeView.render()}
      </div>
    </div>
  );
}
